import React from "react";
import * as UI from "./ui.jsx";
import { useInspectionData } from "./inspection_display.js";
// Horse profile — six tabs; Box & live view is the box-camera history

const { useState: useStateP } = React;

function HorseProfile({ horseId, go }) {
  const { Icon, Tag, Card, Sparkline, Stat, BoxSnap, useBoxAssign, ModuleGate } = UI;
  const h = window.DATA.HORSES.find(x => x.id === horseId);
  const [tab, setTab] = useStateP("overview");
  const showInspections = useInspectionData();
  // The Inspections tab can stop existing while it is the selected one, if an
  // Owner turns the figures off on another screen. Falling back to Overview
  // keeps a real tab lit rather than leaving the page blank under a tab strip
  // that no longer offers what is selected.
  React.useEffect(() => {
    if (!showInspections && tab === "inspections") setTab("overview");
  }, [showInspections, tab]);
  const [assign, setAssign] = useBoxAssign();
  if (!h) return <div>Not found.</div>;

  const box = assign[h.lot];
  const insp = window.DATA.INSPECTIONS.filter(i => i.horses.includes(h.id));
  const freeBoxes = window.DATA.BOX_IDS.filter(b => !Object.values(assign).includes(b) || assign[h.lot] === b);
  const hasCameras = window.VD.source("stable.camera_media") !== "unavailable";
  const hasStats = h.statsAvailable;

  const TABS = [["overview", "Overview"]]
    .concat(hasStats ? [["stats", "Stats & trends"]] : [])
    .concat(showInspections ? [["inspections", `Inspections · ${insp.length}`]] : [])
    .concat(hasCameras ? [["media", "Box & live view"]] : [])
    .concat(hasStats ? [["xrays", `X-rays · ${h.xrays}`], ["parades", `Parades · ${h.parades}`]] : []);

  return (
    <div className="fade-in">
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="profile-grid hero-grid">
          <div style={{ padding: "26px 30px" }}>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <span className="eyebrow">LOT {h.lot}</span>
              <Tag>{h.colour} {h.sex}</Tag>
              <Tag>Foaled {h.dob}</Tag>
              {h.bonus && h.bonus.split(", ").map(b => <Tag key={b} variant="gold">{b.replace(" Eligible", "")}</Tag>)}
              {h.withdrawn && <Tag variant="ribbon">Withdrawn</Tag>}
              {!h.withdrawn && h.hot && <Tag variant="gold">★ Hot interest</Tag>}
            </div>
            <div className="display" style={{ fontSize: "clamp(30px, 4vw, 46px)", marginTop: 10, lineHeight: 1.05 }}>{h.name}</div>
            <div className="display-italic" style={{ fontSize: 16, color: "var(--inglis-blue-dark)", marginTop: 10 }}>
              Unnamed 2YO · by {h.sire} ex {h.dam} ({h.damSire})
            </div>
            {hasCameras && (
              <div className="row" style={{ marginTop: 14, gap: 10, alignItems: "center" }}>
                <span className="muted" style={{ fontSize: 13 }}>Box</span>
                <select value={box || ""} onChange={e => {
                  const next = { ...assign };
                  if (e.target.value) next[h.lot] = +e.target.value; else delete next[h.lot];
                  setAssign(next);
                }} style={{ padding: "8px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 14, background: "var(--white)" }}>
                  <option value="">Unassigned</option>
                  {freeBoxes.map(b => <option key={b} value={b}>K{b} · {window.DATA.camForBox(b)}</option>)}
                </select>
                <span className="muted" style={{ fontSize: 12 }}>Selling {window.DATA.ringTime(+h.lot)} tomorrow</span>
              </div>
            )}
            <div className="row" style={{ marginTop: 20, gap: 10, flexWrap: "wrap" }}>
              {window.VD.reports?.enabled && <button className="btn" onClick={() => go("report:" + h.id)}>Create lot report</button>}
              <button className="btn primary" onClick={() => go("inspect:" + h.id)}><Icon.plus style={{ width: 16, height: 16 }}/> New inspection</button>
              {hasCameras && <button className="btn" onClick={() => setTab("media")}><Icon.camera style={{ width: 16, height: 16 }}/> Watch box live</button>}
            </div>
          </div>
          <div style={{ borderLeft: "1px solid var(--divider)", position: "relative", minHeight: 260 }}>
            <BoxSnap box={box} lotLabel={`LOT ${h.lot}`} style={{ position: "absolute", inset: 0 }}/>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 22, marginBottom: 18, overflowX: "auto" }}>
        <div className="seg">
          {TABS.map(([id, lbl]) => (
            <button key={id} className={tab === id ? "on" : ""} onClick={() => setTab(id)}>{lbl}</button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <div className="grid cols-3 profile-cols">
          <Card title="Draft stats" style={{ gridColumn: "span 2" }}>
            {hasStats ? (
              <>
                <div className="grid cols-4 statgrid">
                  <Stat label="Website views" value={h.views.toLocaleString()}/>
                  <Stat label="Breeze plays" value={h.plays}/>
                  <Stat label="X-ray views" value={h.xrays}/>
                  <Stat label="On watchlist" value={h.watchlist}/>
                  {showInspections && <Stat label="Inspections" value={h.inspections}/>}
                  <Stat label="Parades" value={h.parades}/>
                  <Stat label="Box" value={box ? `K${box}` : "—"}/>
                  <Stat label="Sells" value={window.DATA.ringTime(+h.lot)}/>
                </div>
                <div className="divider"/>
                <div className="eyebrow" style={{ marginBottom: 8 }}>Interest trajectory</div>
                <div style={{ height: 110 }}><Sparkline data={h.trend} height={110}/></div>
              </>
            ) : (
              <div className="muted" style={{ fontSize: 14 }}>Vendor stats are not loaded in this environment.</div>
            )}
          </Card>
          {showInspections && <Card title="Today's inspections">
            {insp.length === 0 && <div className="muted">None recorded today.</div>}
            {insp.map((i, idx) => (
              <div key={i.id} style={{ padding: "12px 0", borderTop: idx ? "1px solid var(--divider)" : "0" }}>
                <div className="mono" style={{ color: "var(--fg-muted)", fontSize: 12 }}>{i.time}</div>
                <div style={{ fontSize: 13.5, marginTop: 4 }}>{i.buyer}{i.repeat && <span style={{ marginLeft: 8 }}><Tag variant="gold">Repeat</Tag></span>}</div>
                {i.agent && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{i.agent}</div>}
              </div>
            ))}
          </Card>}
        </div>
      )}

      {tab === "stats" && (
        <div className="grid cols-2">
          {[["Website views", h.views.toLocaleString(), `Across the ${window.DATA.SALE.code} listing on inglis.com.au`],
            ["Breeze plays", h.plays, "Breeze-up video · official times"],
            ["X-ray views", h.xrays, "Repository downloads & in-app views"],
            ["Watchlist adds", h.watchlist, "Buyers who bookmarked this lot"]].map(([label, v, sub]) => (
            <div key={label} className="card">
              <div className="eyebrow">{label}</div>
              <div className="display" style={{ fontSize: 54, marginTop: 6, lineHeight: 1 }}>{v}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{sub}</div>
              <div style={{ marginTop: 16, height: 90 }}><Sparkline data={h.trend} height={90}/></div>
            </div>
          ))}
        </div>
      )}

      {tab === "inspections" && (
        <ModuleGate modules="inspections">
          <Card className="flush">
            <table className="table">
              <thead><tr><th>Time</th><th>Inspector</th><th>Agent</th><th>Lots in visit</th><th></th></tr></thead>
              <tbody>
                {insp.length === 0 && <tr><td colSpan="5" style={{ textAlign: "center", padding: 40, color: "var(--fg-muted)" }}>No inspections logged yet.</td></tr>}
                {insp.map(i => (
                  <tr key={i.id}>
                    <td className="mono">{i.time}</td>
                    <td style={{ color: "var(--fg-1)", fontSize: 15 }}>{i.buyer}</td>
                    <td>{i.agent || "—"}</td>
                    <td>{i.horses.map(id => "Lot " + (window.DATA.HORSES.find(x => x.id === id)?.lot)).join(", ")}</td>
                    <td style={{ textAlign: "right" }}>{i.repeat && <Tag variant="gold">Repeat</Tag>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </ModuleGate>
      )}

      {tab === "media" && <SnapshotHistory h={h} box={box}/>}

      {tab === "xrays" && (
        <Card title="Radiograph repository" subtitle={`${h.xrays} views by vets this sale`}>
          <div className="muted" style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 560 }}>
            Full repository set lodged with Inglis — viewable to registered vets through the sale platform.
            View counts roll up into the draft stats above.
          </div>
        </Card>
      )}

      {tab === "parades" && (
        <Card title="Parade history" subtitle={`${h.parades} walks for buyers this week · Parade K lane`}>
          {Array.from({ length: h.parades }).map((_, i) => (
            <div key={i} className="row" style={{ justifyContent: "space-between", padding: "13px 0", borderTop: i ? "1px solid var(--divider)" : "0" }}>
              <div className="row" style={{ gap: 14 }}>
                <div className="display" style={{ fontSize: 21, color: "var(--inglis-blue)", width: 36 }}>{String(i + 1).padStart(2, "0")}</div>
                <div>{["Mon 12 Oct · 07:40", "Mon 12 Oct · 17:20", "Tue 13 Oct · 07:15", "Tue 13 Oct · 08:30", "Tue 13 Oct · 17:45", "Wed 14 Oct · 07:20", "Wed 14 Oct · 07:55"][i] || "Earlier this week"}</div>
              </div>
              <Tag>Complete</Tag>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// Box view — live-first: an on-demand cropped live stream of the box, with the
// ~5-minute thumbnails as the archive below. Stream idles out ~90 s after leaving.
function SnapshotHistory({ h, box }) {
  const { Card, BoxSnap, Tag } = UI;
  const [range, setRange] = useStateP("2h");
  const [live, setLive] = useStateP(true);
  if (!box) return <Card title="Box view"><div className="muted">Assign Lot {h.lot} to a box to start collecting snapshots.</div></Card>;
  const counts = { "2h": 8, "today": 12, "week": 12 };
  const stepMin = { "2h": 15, "today": 40, "week": 240 }[range];
  const nowM = window.DATA.now().getHours() * 60 + window.DATA.now().getMinutes();
  const items = Array.from({ length: counts[range] }, (_, i) => {
    const m = Math.max(0, nowM - i * stepMin);
    return { t: `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`, i };
  });
  return (
    <div>
      <Card style={{ padding: 0, overflow: "hidden" }} className="flush">
        <div style={{ position: "relative", aspectRatio: "16/7", cursor: "pointer" }} onClick={() => setLive(v => !v)}>
          <BoxSnap box={box} lotLabel={`LOT ${h.lot}`} live={live} style={{ position: "absolute", inset: 0 }}/>
          {!live && (
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.35)" }}>
              <span className="btn" style={{ background: "#fff", borderColor: "#fff", color: "var(--inglis-blue)" }}>▶ Watch box live</span>
            </div>
          )}
        </div>
        <div className="row" style={{ padding: "13px 18px", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>Box K{box} · {window.DATA.camForBox(box)}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{live ? "Live cropped stream · stops ~90 s after you leave" : "Tap to start the live stream on demand"}</div>
          </div>
          {live ? <Tag variant="live">Live</Tag> : <Tag>Idle</Tag>}
        </div>
      </Card>
      <div className="section-head" style={{ marginTop: 24 }}>
        <h2>Snapshot archive · ~{window.DATA.SNAPSHOT_INTERVAL} min</h2>
        <div className="seg">
          {[["2h", "Last 2 hours"], ["today", "Today"], ["week", "This week"]].map(([id, lbl]) => (
            <button key={id} className={range === id ? "on" : ""} onClick={() => setRange(id)}>{lbl}</button>
          ))}
        </div>
      </div>
      <div className="grid cols-4">
        {items.map(s => (
          <div key={s.i} className="card" style={{ padding: 0, overflow: "hidden" }}>
            <BoxSnap box={box} meta={false} style={{ height: 140 }}/>
            <div style={{ padding: "9px 13px" }} className="row">
              <span style={{ fontSize: 12 }}>{window.DATA.camForBox(box)}</span>
              <span className="mono muted" style={{ fontSize: 11, marginLeft: "auto" }}>{s.t}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { HorseProfile };
